/**
 * React Aria documentation
 * Disclosure: https://react-spectrum.adobe.com/react-aria/Disclosure.html
 * DisclosureGroup: https://react-spectrum.adobe.com/react-aria/DisclosureGroup.html
 */
import React, { useContext } from 'react';
import {
  Disclosure as AriaDisclosure,
  DisclosureGroup as AriaDisclosureGroup,
  type DisclosureProps as AriaDisclosureProps,
  type DisclosureGroupProps as AriaDisclosureGroupProps,
  DisclosurePanel as AriaDisclosurePanel,
  type DisclosurePanelProps as AriaDisclosurePanelProps,
  composeRenderProps,
  Heading,
  type HeadingProps,
  Button,
  DisclosureStateContext,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from './component-utils';
import { DisclosureGroupStateContext } from 'react-aria-components';
import { LabAddIcon } from 'components/camperlab/icons/add';
import { LabMinusIcon } from 'components/camperlab/icons/minus';
import { isDomain } from 'utils/domain';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { LabPlusMinusIcon } from 'components/camperlab/icons/lab-plus-minus-icon';

const disclosure = tv({
  base: 'group min-w-64 border-b border-subtle lab:border-b-0',
  // variants: {
  //   isInGroup: {
  //     true: 'border-0 border-b',
  //   },
  // },
});

const disclosureButton = tv({
  extend: focusRing,
  base: 'flex w-full cursor-default items-center justify-between gap-2 px-3 py-4 text-start text-sm font-normal lab:px-0 lab:py-0 lab:font-bold lab:uppercase lg:text-xs',
  variants: {
    isDisabled: {
      true: 'text-disabled forced-colors:text-[GrayText]',
    },
    isInGroup: {
      true: '-outline-offset-2',
    },
  },
});

const disclosureIcon = tv({
  base: 'size-3.5 flex-shrink-0 transition duration-200 ease-in-out',
  variants: {
    // isExpanded: {
    //   true: 'rotate-90 transform',
    // },
    isDisabled: {
      true: 'text-disabled forced-colors:text-[GrayText]',
    },
  },
});

export type DisclosureProps = AriaDisclosureProps & {
  children: React.ReactNode;
};

export function Disclosure({ children, ...props }: DisclosureProps) {
  // const isInGroup = useContext(DisclosureGroupStateContext) !== null;
  return (
    <AriaDisclosure {...props} className={composeRenderProps(props.className, (className, renderProps) => disclosure({ ...renderProps, className }))}>
      {children}
    </AriaDisclosure>
  );
}

function DisclosureHeaderIcon({ isExpanded, isDisabled }: { isExpanded: boolean; isDisabled: boolean }) {
  if (isDomain('camperlab')) {
    return <LabPlusMinusIcon isExpanded={isExpanded} aria-hidden className={disclosureIcon({ isDisabled })} />;
  }

  return (
    <ChevronSVG
      data-active={isExpanded}
      data-disabled={isDisabled}
      className="transition-transform duration-150 ease-in-out will-change-transform data-[active=true]:rotate-180 data-[disabled=true]:text-disabled"
    />
  );
}

export type DisclosureHeaderProps = React.PropsWithChildren<HeadingProps & { buttonClassName?: string }>;

export function DisclosureHeader({ children, buttonClassName, ...rest }: DisclosureHeaderProps) {
  const { isExpanded } = useContext(DisclosureStateContext)!;
  const isInGroup = useContext(DisclosureGroupStateContext) !== null;
  return (
    <Heading {...rest} className="mb-0">
      <Button slot="trigger" className={(renderProps) => disclosureButton({ ...renderProps, isInGroup, className: buttonClassName })}>
        {({ isDisabled }) => (
          <>
            {children}
            <DisclosureHeaderIcon isExpanded={isExpanded} isDisabled={isDisabled} />
          </>
        )}
      </Button>
    </Heading>
  );
}

export interface DisclosurePanelProps extends AriaDisclosurePanelProps {
  children: React.ReactNode;
}

export function DisclosurePanel({ children, ...props }: DisclosurePanelProps) {
  return (
    <AriaDisclosurePanel
      {...props}
      className={composeTailwindRenderProps(props.className, 'px-3 group-data-[expanded]:pb-5 group-data-[expanded]:pt-2.5 lab:px-0')}
    >
      {children}
    </AriaDisclosurePanel>
  );
}

export interface DisclosureGroupProps extends AriaDisclosureGroupProps {
  children: React.ReactNode;
}

export function DisclosureGroup({ children, ...props }: DisclosureGroupProps) {
  return (
    <AriaDisclosureGroup {...props} className={composeTailwindRenderProps(props.className, `flex flex-col lab:gap-0`)}>
      {children}
    </AriaDisclosureGroup>
  );
}
